import type { AccountSettings, SetLeverageResponse } from '@/types/generated/public_api'

/** Fold a set_leverage echo into the cached settings. Nothing cached: the next fetch fills it. */
export function applyLeverageEcho(
  prev: AccountSettings | undefined,
  echo: SetLeverageResponse
): AccountSettings | undefined {
  if (!prev) return undefined
  return { ...prev, leverage: { ...prev.leverage, [echo.instrument]: echo.leverage } }
}

/**
 * The leverage the venue will apply to the symbol: the stored value, else the venue default,
 * clamped to the instrument ceiling (the venue clamps too). Null hides the chip: no default
 * (a venue without per-instrument leverage) or no ceiling.
 */
export function displayedLeverage(
  s: AccountSettings | undefined,
  symbol: string,
  maxLeverage?: string
): number | null {
  if (!s || s.defaultLeverage === undefined) return null
  const max = Number(maxLeverage)
  if (!maxLeverage || !Number.isFinite(max) || max < 1) return null
  const value = s.leverage[symbol] ?? s.defaultLeverage
  return Math.min(value, Math.floor(max))
}

/**
 * Leverage typed into the chip: whole number clamped to 1..max. Null for an empty or
 * non-numeric draft (Save stays disabled); the clamp is applied on blur and save only, so
 * clearing the box and typing 5 yields 5, not 15.
 */
export function parseLeverageDraft(raw: string, max: number): number | null {
  if (raw.trim() === '') return null
  const n = Math.round(Number(raw))
  if (!Number.isFinite(n)) return null
  return Math.min(max, Math.max(1, n))
}
